import { merge, isNil } from 'lodash-es'
import { compareValuesNullsLast } from '@/components/shared-table/utils'

const prepareSmiles = (structures) => {
  return structures.map((c) => c?.smiles).filter(Boolean)
}

const chemicalIdentifierKeys = [
  // 'uuid',
  'casrn',
  'sid',
  'cid',
  'inchiKey',
  'inchi',
  'smiles',
  'chemId',
  'canonicalSmiles',
]

const getChemicalIdentifier = (chemical, { avoidKeys = [] } = {}) => {
  if (!chemical) return undefined

  for (const key of chemicalIdentifierKeys) {
    if (avoidKeys.includes(key)) continue
    if (chemical[key]) return chemical[key]
  }
}

const getChemicalUniqueKey = (chemical) => {
  if (!chemical) return undefined

  const identifier = getChemicalIdentifier(chemical)
  if (!isNil(chemical?.inputOrder) && identifier) return `${chemical?.inputOrder}-${identifier}`

  if (identifier) return identifier

  return chemical?.inputOrder
}

const getChemicalDomId = (chemical) => {
  const key = getChemicalUniqueKey(chemical)
  return key ? key.replace(/[^a-zA-Z0-9_-]/g, '_') : ''
}

const getChemicalTitle = (chemical) => chemical?.name || getChemicalIdentifier(chemical)

const getChemicalTitleExceptCasrn = (chemical) =>
  chemical?.name || chemical?.inchiKey || getChemicalIdentifier(chemical, { avoidKeys: ['casrn'] })

const getChemicalCasrnWithFallBack = (chemical, fallback = 'No CAS') => chemical?.casrn || fallback

const prepareChemicals = (structures, getIdentifier = getChemicalUniqueKey) =>
  structures.map((structure) => ({
    id: getIdentifier(structure),
    smiles: structure?.smiles,
  }))

const fillChemicalData = ({
  chemical,
  sentStructures,
  getReceivedIdentifier = getChemicalUniqueKey,
  getSentIdentifier = getChemicalUniqueKey,
} = {}) => {
  if (!chemical || !sentStructures || !sentStructures.length) return chemical

  const chemicalIdentifier = getReceivedIdentifier(chemical)

  if (!chemicalIdentifier) return chemical

  const matchingSentStructure = sentStructures.find((structure) => getSentIdentifier(structure) === chemicalIdentifier)

  const sentInchiKey = matchingSentStructure?.inchiKey
  const chemicalInchiKey = chemical?.inchiKey

  const extractIdField = (chem) => (chem?.id ? { id: chem.id } : {})

  if (matchingSentStructure) {
    if (!isNil(sentInchiKey)) {
      if (sentInchiKey === chemicalInchiKey) return merge({}, chemical, matchingSentStructure, extractIdField(chemical))
    }
    return merge({}, matchingSentStructure, extractIdField(chemical))
  }
  return chemical
}

const checkIsChemicalIsWithSmiles = (smilesArray, chemical) => {
  if (!smilesArray?.length || !chemical) return false
  const chemicalSmiles = [chemical?.smiles, chemical?.canonicalSmiles].filter(Boolean)
  if (!chemicalSmiles.length) return false
  return Array.isArray(smilesArray)
    ? smilesArray.some((s) => chemicalSmiles.includes(s))
    : chemicalSmiles.includes(smilesArray)
}

const findChemicalBySmilesArray = (smilesArray, chemicals) => {
  if (!smilesArray?.length || !chemicals?.length) return null
  return chemicals.find((chemical) => checkIsChemicalIsWithSmiles(smilesArray, chemical)) || null
}

const enrichResponseWithSentStructuresBySmiles = ({ responseData, sentStructures, resultKey }) => {
  if (!Array.isArray(responseData)) return responseData

  const fillChemicalsResponse = (response) =>
    response.map((responseItem) => {
      const smilesArrayInResponse = [responseItem?.smiles, responseItem?.chemical, responseItem?.standardized].filter(
        Boolean,
      )
      return {
        ...(resultKey
          ? { [resultKey]: { ...responseItem } }
          : { ...responseItem, originalChemical: responseItem?.chemical }),
        chemical: findChemicalBySmilesArray(smilesArrayInResponse, sentStructures),
      }
    })

  return fillChemicalsResponse(responseData)
}

const compareChemicals = (a, b, isDescending) => {
  const aTitle = getChemicalTitle(a)
  const bTitle = getChemicalTitle(b)
  return compareValuesNullsLast(aTitle, bTitle, isDescending)
}

const getStandardizationChange = (standardizedChemical, chemical) => {
  if (!standardizedChemical) return null

  const standardizedInchiKey = standardizedChemical?.inchiKey
  const chemicalInchiKey = chemical?.inchiKey

  const checkInchiKeyStructuresEquality = (inchiKey1, inchiKey2) => {
    const getStructureFromInchiKey = (inchiKey) => {
      if (!inchiKey) return null
      const splittedInchiKey = inchiKey?.split('-')
      if (splittedInchiKey?.length < 2) return null
      const [structure] = splittedInchiKey
      return structure
    }
    const structure1 = getStructureFromInchiKey(inchiKey1)
    const structure2 = getStructureFromInchiKey(inchiKey2)
    if (!structure1 || !structure2) return false
    return structure1 === structure2
  }

  const checkInchiKeyEquality = (inchiKey1, inchiKey2) => {
    if (!inchiKey1 || !inchiKey2) return false
    return inchiKey1 === inchiKey2
  }
  if (checkInchiKeyEquality(standardizedInchiKey, chemicalInchiKey)) return null

  return {
    chemical: standardizedChemical,
    message: checkInchiKeyStructuresEquality(standardizedInchiKey, chemicalInchiKey)
      ? 'Stereochemistry was changed or removed during standardization'
      : 'Chemical was changed during standardization',
  }
}

export {
  getChemicalDomId,
  getChemicalUniqueKey,
  getChemicalIdentifier,
  getChemicalTitle,
  getChemicalTitleExceptCasrn,
  fillChemicalData,
  enrichResponseWithSentStructuresBySmiles,
  prepareSmiles,
  prepareChemicals,
  compareChemicals,
  getStandardizationChange,
  getChemicalCasrnWithFallBack,
}
